/* eslint-disable react/no-array-index-key */
import {Typography, css, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  DlMuiComponentBox,
  GetStylesFunction,
  i18nContainer,
  IconHeaderWithSearch,
  mixins,
  StringUtils,
  utils,
  ExportUtils,
  useCurrentLocale,
  DlMuiButton,
} from '@wallaby-ui/core';
import {useAppName, useFormatters, usePermissions} from '@wallaby-ui/document';
import * as React from 'react';
import {useCallback} from 'react';
import {Helmet} from 'react-helmet';
import {defineMessages, useIntl} from 'react-intl';

const I18N = defineMessages({
  PAGE_TITLE: {
    id: 'userSettings.pageTitle',
    defaultMessage: 'User settings',
    description: 'title for settings page',
  },
});

interface OwnProps {
  config: ConfigObjectType;
}

type Props = OwnProps;

export const SettingsLayout: React.FC<Props> = (props) => {
  const intl = useIntl();
  const theme = useTheme();
  const styles = getStyles({theme});

  const {config} = props;
  const {t, tEnum} = useFormatters();
  const {departments, departmentRoleStrings, userRoleStrings} = usePermissions(config);
  const {appName} = useAppName();
  const roleEnumName = appName === 'HAIDI' ? 'HaidiRoleEnum' : 'MerieRoleEnum';

  const currentLocale = useCurrentLocale();
  const downloadUserGuidePdf = useCallback(() => {
    ExportUtils.handleExportPdf({
      url: config.service.url.userGuidePdf,
      filters: {},
      locale: currentLocale,
    });
  }, [config.service.url.userGuidePdf, currentLocale]);
  const showUserGuideDownload = appName === 'MERIE' && false;
  return (
    <div data-name={'settings-layout--content-wrapper'} css={styles.contentWrapper}>
      <Helmet title={utils.TranslateUtils.translate(I18N.PAGE_TITLE, intl)} />
      <div css={styles.content}>
        <div css={styles.contentBoxWrapper}>
          <div css={styles.contentBox}>
            <div css={styles.wrapper}>
              <IconHeaderWithSearch iconCss={styles.icon} iconName={'settingsIcon'} useFixedViewBox={16} label={'userSettings.pageTitle'} />
              <DlMuiComponentBox isOverflow={false} noTopPadding inputCss={styles.componentBox}>
                <div css={styles.settingsWrapper}>
                  <Typography variant={'label'} css={styles.groupLabel}>
                    {t('login.userSettings')}
                  </Typography>
                  <i18nContainer.LanguageSwitcherContainer />
                </div>
                <div css={styles.departmentsWrapper}>
                  <Typography variant={'label'} css={styles.groupLabel}>
                    {t('permissions.departments')}
                  </Typography>
                  <ul css={styles.departmentsList}>
                    {departments.map((v, i) => {
                      const groupVariants = ['Skupina', 'Grupa', 'Gruppe', 'Group'];
                      const isGroup = (value: string) => groupVariants.includes(value);
                      return (
                        <li key={i}>
                          {departments.length === 1 && isGroup(departments[0]) ? (
                            <Typography variant={'value'}>{t('permissions.departments.group')}</Typography>
                          ) : (
                            <Typography variant={'value'}>{v}</Typography>
                          )}
                        </li>
                      );
                    })}
                    {departmentRoleStrings.map((prefixedKey) => {
                      const key = StringUtils.removePrefix(prefixedKey, 'DEPARTMENT:');
                      let enumLabel = '';
                      switch (key) {
                        case 'LIMITED_PERMISSIONS':
                        case 'UNLIMITED_PERMISSIONS':
                          enumLabel = tEnum('DepartmentPermissionsTypeEnum', key);
                          break;
                      }
                      const translationKey = `permissions.${prefixedKey}`;
                      const translatedLabel = t(translationKey);
                      let label = enumLabel;
                      if (!label || label === key) {
                        label = translatedLabel;
                      }
                      if (!label || label === translationKey) {
                        label = key;
                      }
                      return (
                        <li key={prefixedKey}>
                          <Typography variant={'value'}>{label}</Typography>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div css={styles.permissionsWrapper}>
                  <Typography variant={'label'} css={styles.groupLabel}>
                    {t('permissions.roles')}
                  </Typography>
                  <ul css={styles.permissionsList}>
                    {userRoleStrings.map((k) => {
                      const tranlationKey = k;
                      const translatedLabel = tEnum(roleEnumName, tranlationKey);
                      return (
                        <li key={k}>
                          <Typography variant={'value'}>{translatedLabel !== tranlationKey ? translatedLabel : k}</Typography>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                {showUserGuideDownload && (
                  <DlMuiButton
                    type={'chip-filled'}
                    title={'userGuide.pdf.title'}
                    css={styles.button}
                    onClick={() => downloadUserGuidePdf()}
                  />
                )}
              </DlMuiComponentBox>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    ${mixins.contentWrapper};
    padding: ${options?.theme?.spacing(3, 4, 3, 0)};
  `,
  content: css`
    ${mixins.content};
    gap: ${options?.theme?.spacing(4)};
  `,
  contentBoxWrapper: css`
    ${mixins.contentBoxWrapper};
    height: 100%;
    overflow: hidden;
    gap: ${options?.theme?.spacing(3)};
  `,
  contentBox: css`
    ${mixins.contentBox};
    height: 100%;
    overflow: hidden;
    flex-direction: row;
    gap: ${options?.theme?.spacing(4)};
    border-radius: ${options?.theme?.borderRadius};
  `,
  wrapper: css`
    background-color: #ffffff;
    gap: ${options?.theme?.spacing(2.5)};
    display: flex;
    flex-direction: column;
    padding: ${options?.theme?.spacing(4)};
    align-items: flex-start;
    border-radius: ${options?.theme?.spacing(4)};
  `,
  icon: css`
    width: 24px;
    height: 24px;
  `,
  componentBox: css`
    gap: 8px;
  `,
  departmentsWrapper: css`
    display: flex;
    flex-direction: column;
    align-items: start;
    gap: 4px;
  `,
  departmentsList: css`
    margin: 0;
  `,
  permissionsWrapper: css`
    display: flex;
    flex-direction: column;
    align-items: start;
    gap: 4px;
  `,
  permissionsList: css`
    margin: 0;
  `,
  settingsWrapper: css`
    display: flex;
    flex-direction: column;
    align-items: start;
    gap: 4px;
    margin-bottom: 16px;
  `,
});
